import { useEffect, useState } from "react";
import { Calculator, Gift } from "lucide-react";
import api from "@/lib/api";
import { cadStr } from "@/lib/pricing";

const QUICK = ["20", "50", "100", "250", "1000"];

function Diff({ row }) {
  if (row.current) return <span className="text-slate-400">Your plan</span>;
  const v = Number(row.fee_vs_current_cad);
  if (v === 0) return <span className="text-slate-500">Same fee as your plan</span>;
  const abs = Math.abs(v).toFixed(2);
  return v < 0
    ? <span className="text-emerald-700 font-semibold">{cadStr(abs)} less per sale</span>
    : <span className="text-slate-500">{cadStr(abs)} more per sale</span>;
}

function PlanResult({ row }) {
  return (
    <div data-testid={`fee-calc-plan-${row.id}`}
         className={`rounded-xl border p-4 transition-colors ${row.current ? "border-cyan-400 bg-cyan-50/60" : "border-slate-200 bg-white"}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-display text-lg text-slate-900">{row.name}</span>
        {row.current && <span className="text-[10px] font-bold uppercase tracking-widest bg-cyan-600 text-white rounded-full px-2 py-0.5" data-testid={`fee-calc-plan-${row.id}-current`}>Your plan</span>}
      </div>
      <div className="text-[11px] text-slate-500">Minimum fee {cadStr(row.fee_minimum_cad)} per sale</div>
      <div className="mt-3 text-[11px] text-slate-500">Performance fee</div>
      <div className="font-display text-2xl text-slate-900" data-testid={`fee-calc-plan-${row.id}-fee`}>{row.fee_pct}%</div>
      {row.minimum_applied && (
        <div className="text-[11px] text-amber-700" data-testid={`fee-calc-plan-${row.id}-minimum`}>
          Minimum {cadStr(row.fee_minimum_cad)} applies on this sale
        </div>
      )}
      <div className="mt-2 text-xs text-slate-600">You keep <strong data-testid={`fee-calc-plan-${row.id}-keeps`}>{cadStr(row.merchant_keeps_cad)}</strong></div>
      <div className="mt-1 text-xs" data-testid={`fee-calc-plan-${row.id}-diff`}><Diff row={row} /></div>
    </div>
  );
}

// Live fee + cashback for one eligible sale on every plan. All numbers come from the server.
export function FeeCalculatorCard() {
  const [amount, setAmount] = useState("100");
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    const v = Number(amount);
    if (!amount || !(v > 0) || v > 100000) {
      setD(null);
      setErr(amount ? "Enter a sale between CA$0.01 and CA$100,000." : null);
      return undefined;
    }
    let live = true;
    const t = setTimeout(() => {
      api.get("/merchant/fee-calculator", { params: { sale_amount: v.toFixed(2) } })
        .then((r) => { if (live) { setD(r.data); setErr(null); } })
        .catch((e) => {
          const msg = e?.response?.data?.detail;
          if (live) setErr(typeof msg === "string" ? msg : "Enter a valid sale amount.");
        });
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [amount]);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="fee-calculator-card">
      <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold inline-flex items-center gap-2">
        <Calculator className="w-3.5 h-3.5" /> Fee and cashback calculator
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <label className="relative" htmlFor="fee-calc-amount">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">CA$</span>
          <input id="fee-calc-amount" type="number" inputMode="decimal" min="0.01" max="100000" step="0.01" value={amount}
                 onChange={(e) => setAmount(e.target.value)} data-testid="fee-calc-amount-input" aria-label="Sale amount in CAD"
                 className="w-40 rounded-full border border-slate-300 pl-12 pr-4 py-2 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-cyan-400" />
        </label>
        <div className="flex flex-wrap gap-1.5">
          {QUICK.map((q) => (
            <button key={q} type="button" onClick={() => setAmount(q)} data-testid={`fee-calc-quick-${q}`}
                    className={`px-3 py-1.5 rounded-full border text-xs font-semibold transition-colors ${amount === q ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-700 hover:border-cyan-400"}`}>
              {cadStr(q)}
            </button>
          ))}
        </div>
      </div>
      {err && <div className="mt-3 text-xs text-red-700" data-testid="fee-calc-error">{err}</div>}
      {d && (
        <>
          <div className="mt-5 grid sm:grid-cols-3 gap-3" data-testid="fee-calc-results">
            {d.plans.map((row) => <PlanResult key={row.id} row={row} />)}
          </div>
          <div className="mt-4 rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-900 inline-flex items-center gap-2" data-testid="fee-calc-cashback">
            <Gift className="w-4 h-4 shrink-0" />
            <span>Your shopper earns <strong>{cadStr(d.shopper_cashback_cad)}</strong> cashback ({d.shopper_cashback_rate_display}) on this sale, the same on every plan.</span>
          </div>
        </>
      )}
      <p className="mt-3 text-[11px] text-slate-500" data-testid="fee-calc-basis">
        Based on the sale subtotal after discounts, before tax and shipping. The fee is the percentage or the minimum, whichever is higher.
      </p>
    </div>
  );
}
